import { useState, useEffect } from 'react'
import api from '../utils/api'
import { useAuth } from '../context/AuthContext'
import { formatDateReadable, formatDate } from '../utils/dateFormatter'
import './Dashboard.css'

const Dashboard = () => {
  const { user, updateProfile } = useAuth()
  const [showEditModal, setShowEditModal] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    first_name: (user?.name || '').split(' ')[0] || '',
    last_name: (user?.name || '').split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    mobile: user?.mobile || '',
    business_name: user?.business_name || '',
    address: user?.address || '',
    logo_url: user?.logo_url || ''
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [tags, setTags] = useState([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(6)

  useEffect(() => {
    fetchMyTags()
    // If name is missing, prompt to fill it
    if (user && !user.name) {
      setShowEditModal(true)
      setError('Please complete your profile by providing your name.')
    }
  }, [user])

  const fetchMyTags = async () => {
    try {
      setLoadingTags(true)
      const response = await api.get('/qrcodes/my-tags')
      if (response.data.success) {
        setTags(response.data.data.qrcodes)
      }
    } catch (err) {
      console.error('Error fetching tags:', err)
    } finally {
      setLoadingTags(false)
    }
  }

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'mobile') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setFormData({
      ...formData,
      [name]: value
    })
    setError('')
    setSuccess('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    const fullName = [formData.first_name, formData.last_name].filter(Boolean).join(' ').trim() || formData.name?.trim()
    if (!fullName) {
      setError('First name or Last name is required')
      setLoading(false)
      return
    }
    if (!formData.email.trim()) {
      setError('Email is required')
      setLoading(false)
      return
    }

    if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
      setError('Mobile number must be exactly 10 digits')
      setLoading(false)
      return
    }

    if (['franchise', 'franchise_tags'].includes(user?.role) && !formData.address?.trim()) {
      setError('Address is mandatory for franchise accounts')
      setLoading(false)
      return
    }

    const result = await updateProfile({ ...formData, name: fullName })
    setLoading(false)

    if (result.success) {
      setSuccess('Profile updated successfully!')
      setTimeout(() => {
        setShowEditModal(false)
        setSuccess('')
      }, 1500)
    } else {
      setError(result.message || 'Failed to update profile')
    }
  }

  const handleOpenModal = () => {
    const nameParts = (user?.name || '').split(' ')
    setFormData({
      name: user?.name || '',
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      business_name: user?.business_name || '',
      address: user?.address || '',
      logo_url: user?.logo_url || ''
    })
    setError('')
    setSuccess('')
    setShowEditModal(true)
  }

  const handleCloseModal = () => {
    setShowEditModal(false)
    setError('')
    setSuccess('')
    const nameParts = (user?.name || '').split(' ')
    setFormData({
      name: user?.name || '',
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      business_name: user?.business_name || '',
      address: user?.address || '',
      logo_url: user?.logo_url || ''
    })
  }

  return (
    <>
      <div className="container profile-page-container animate-fadeIn">
        <div className="profile-page-header">
          <h1 className="text-gradient">My Profile</h1>
          <p className="profile-subtitle">Manage your account information and security settings</p>
        </div>

        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-wrapper">
              <div className="avatar-circle">
                {user?.logo_url ? (
                  <img src={user.logo_url} alt="Profile Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                  user?.name?.charAt(0).toUpperCase() || 'U'
                )}
              </div>
              <button className="avatar-edit-btn" onClick={handleOpenModal} title="Edit Profile">
                Edit
              </button>
            </div>
            <div className="profile-info">
              <h2 className="profile-name">{user?.name || 'User'}</h2>
              <p className="profile-email">{user?.email}</p>
              <div className="profile-role">
                <span>🛡️</span>
                <span>{user?.role ? user.role.replace('_', ' ').charAt(0).toUpperCase() + user.role.replace('_', ' ').slice(1) : 'User'}</span>
              </div>
            </div>
          </div>

          <div className="profile-details-section">
            <div className="section-title-wrapper">
              <h3 className="section-title">Account Details</h3>
            </div>

            <div className="profile-details">
              <div className="profile-detail-item">
                <div className="detail-icon-wrapper">
                  <span>📧</span>
                </div>
                <div className="detail-content">
                  <p className="detail-label">Email Address</p>
                  <p className="detail-value">{user?.email || 'Not provided'}</p>
                </div>
              </div>

              <div className="profile-detail-item">
                <div className="detail-icon-wrapper">
                  <span>📱</span>
                </div>
                <div className="detail-content">
                  <p className="detail-label">Mobile Number</p>
                  <p className="detail-value">{user?.mobile ? `+91 ${user.mobile}` : 'Not provided'}</p>
                </div>
              </div>

              {user?.role && ['franchise', 'franchise_tags'].includes(user.role) && (
                <>
                  <div className="profile-detail-item">
                    <div className="detail-icon-wrapper">
                      <span>🏢</span>
                    </div>
                    <div className="detail-content">
                      <p className="detail-label">Business Name</p>
                      <p className="detail-value">{user?.business_name || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="profile-detail-item">
                    <div className="detail-icon-wrapper">
                      <span>📍</span>
                    </div>
                    <div className="detail-content">
                      <p className="detail-label">Business Address</p>
                      <p className="detail-value">{user?.address || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="profile-detail-item">
                    <div className="detail-icon-wrapper">
                      <span>🖼️</span>
                    </div>
                    <div className="detail-content">
                      <p className="detail-label">Logo URL</p>
                      <p className="detail-value" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                        {user?.logo_url ? (
                          <a href={user.logo_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}>
                            {user.logo_url}
                          </a>
                        ) : (
                          'Not provided'
                        )}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {user?.created_at && (
                <div className="profile-detail-item">
                  <div className="detail-icon-wrapper">
                    <span>📅</span>
                  </div>
                  <div className="detail-content">
                    <p className="detail-label">Member Since</p>
                    <p className="detail-value">
                      {formatDateReadable(user.created_at)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="profile-details-section">
            <div className="section-title-wrapper">
              <h3 className="section-title">My Vehicle Tags</h3>
              {tags.filter(t => t.relationship?.is_owner).length > 0 &&
                <span className="tag-count-badge">{tags.filter(t => t.relationship?.is_owner).length} Total</span>
              }
            </div>

            <div className="my-tags-grid">
              {loadingTags ? (
                <div className="tags-loading">
                  <div className="auth-spinner"></div>
                  <p>Loading your tags...</p>
                </div>
              ) : tags.filter(t => t.relationship?.is_owner).length === 0 ? (
                <div className="no-tags-placeholder">
                  <div className="placeholder-icon">🏷️</div>
                  <p>No owned tags linked to your mobile number.</p>
                  <p className="placeholder-sub">Tags you register as owner will appear here.</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const ownedTags = tags.filter(t => t.relationship?.is_owner);
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentTags = ownedTags.slice(indexOfFirstItem, indexOfLastItem);
                    const totalPages = Math.ceil(ownedTags.length / itemsPerPage);

                    return (
                      <>
                        {currentTags.map(tag => (
                          <div key={tag.id} className="tag-mini-card tag-owner-card">
                            <div className="tag-card-main">
                              <div className="tag-vehicle-icon">
                                {tag.tag_type?.toLowerCase().includes('bike') ? '🏍️' : '🚗'}
                              </div>
                              <div className="tag-card-info">
                                <div className="tag-v-number">{tag.linking?.vehicle_number || 'N/A'}</div>
                                <div className="tag-v-details">
                                  {tag.tag_type} • {tag.linking?.color || 'No Color'}
                                </div>
                                <div className="tag-v-details" style={{ marginTop: '4px', fontSize: '0.8rem', opacity: 0.8 }}>
                                  {tag.linking?.brand_or_details || 'No brand/model info'}
                                </div>
                              </div>
                              <div className="tag-card-status">
                                <span className={`dot ${tag.is_active ? 'active' : 'inactive'}`}></span>
                                {tag.is_active ? 'Active' : 'Disabled'}
                              </div>
                            </div>

                            <div className="tag-owner-details" style={{
                              padding: '0.75rem',
                              borderTop: '1px solid #e2e8f0',
                              background: '#f8fafc',
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.75rem',
                              fontSize: '0.8rem'
                            }}>
                              <div>
                                <div style={{ color: '#64748b', marginBottom: '2px', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Emergency Contact</div>
                                <div style={{ color: '#1e293b', fontWeight: '500' }}>
                                  {tag.linking?.emergency_contact_no ? `+91 ${tag.linking.emergency_contact_no}` : 'Not Set'}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: '#64748b', marginBottom: '2px', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>QR Status</div>
                                <div style={{ color: '#1e293b', fontWeight: '500' }}>
                                  {tag.is_active ? (
                                    <span style={{ color: '#059669' }}>✓ Scannable</span>
                                  ) : (
                                    <span style={{ color: '#dc2626' }}>✗ Disabled</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: '#64748b', marginBottom: '2px', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Linked On</div>
                                <div style={{ color: '#1e293b', fontWeight: '500' }}>
                                  {formatDate(tag.created_at)}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: '#64748b', marginBottom: '2px', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>Owner</div>
                                <div style={{ color: '#059669', fontWeight: '600' }}>
                                  You
                                </div>
                              </div>
                            </div>

                            <div className="tag-card-footer">
                              <span className="tag-uuid" style={{ fontSize: '0.75rem', opacity: 0.7 }}>🔗 {tag.code_uuid}</span>
                            </div>
                          </div>
                        ))}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div style={{
                            gridColumn: '1 / -1',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginTop: '1.5rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e2e8f0'
                          }}>
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '0.5rem',
                                border: '1px solid #e2e8f0',
                                background: currentPage === 1 ? '#f1f5f9' : '#fff',
                                color: currentPage === 1 ? '#94a3b8' : '#1e293b',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}
                            >
                              ← Previous
                            </button>

                            <div style={{
                              display: 'flex',
                              gap: '0.25rem',
                              alignItems: 'center'
                            }}>
                              {[...Array(totalPages)].map((_, index) => (
                                <button
                                  key={index + 1}
                                  onClick={() => setCurrentPage(index + 1)}
                                  style={{
                                    width: '2.5rem',
                                    height: '2.5rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #e2e8f0',
                                    background: currentPage === index + 1 ? 'var(--primary-color)' : '#fff',
                                    color: currentPage === index + 1 ? '#fff' : '#1e293b',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {index + 1}
                                </button>
                              ))}
                            </div>

                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '0.5rem',
                                border: '1px solid #e2e8f0',
                                background: currentPage === totalPages ? '#f1f5f9' : '#fff',
                                color: currentPage === totalPages ? '#94a3b8' : '#1e293b',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}
                            >
                              Next →
                            </button>
                          </div>
                        )}

                        {/* Page Info */}
                        <div style={{
                          gridColumn: '1 / -1',
                          textAlign: 'center',
                          color: '#64748b',
                          fontSize: '0.875rem',
                          marginTop: '0.5rem'
                        }}>
                          Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, ownedTags.length)} of {ownedTags.length} tags
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          <div className="profile-details-section" style={{ marginTop: '2rem' }}>
            <div className="section-title-wrapper">
              <h3 className="section-title">Emergency Contact Vehicles</h3>
              <span className="tag-count-badge warning" style={{ backgroundColor: '#fff3e0', color: '#e65100' }}>
                {tags.filter(t => t.relationship?.is_emergency_contact).length} Linked
              </span>
            </div>

            <div className="my-tags-grid">
              {loadingTags ? (
                <div className="tags-loading">
                  <div className="auth-spinner"></div>
                  <p>Loading...</p>
                </div>
              ) : tags.filter(t => t.relationship?.is_emergency_contact).length === 0 ? (
                <div className="no-tags-placeholder">
                  <div className="placeholder-icon">🆘</div>
                  <p>No vehicles have listed you as emergency contact.</p>
                </div>
              ) : (
                tags.filter(t => t.relationship?.is_emergency_contact).map(tag => (
                  <div key={tag.id} className="tag-mini-card emergency-card" style={{ borderLeft: '4px solid #ff9800' }}>
                    <div className="tag-card-main">
                      <div className="tag-vehicle-icon">
                        {tag.tag_type?.toLowerCase().includes('bike') ? '🏍️' : '🚗'}
                      </div>
                      <div className="tag-card-info">
                        <div className="tag-v-number">{tag.linking?.vehicle_number || 'N/A'}</div>
                        <div className="tag-v-details">Owner: {tag.linking?.mobile_no ? `+91 ${tag.linking.mobile_no}` : 'Hidden'}</div>
                      </div>
                      <div className="tag-card-status">
                        <span className="badge-warning" style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px', background: '#ffe0b2', color: '#e65100' }}>Emerg. Contact</span>
                      </div>
                    </div>
                    <div className="tag-card-footer">
                      <span className="tag-uuid">{tag.code_uuid}</span>
                      <span className="tag-date">Vehicle: {tag.linking?.brand_or_details || 'N/A'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="profile-actions">
            <button className="btn-edit-profile" onClick={handleOpenModal}>
              Edit Profile Settings
            </button>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
                  {success}
                </div>
              )}

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="first_name">First Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Last Name *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  placeholder="Enter your email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile Number (10 digits)</label>
                <div className="mobile-input-wrapper-dashboard" style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-input)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                  <span style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.05)', borderRight: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)', fontWeight: 600 }}>+91</span>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    maxLength="10"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="9876543210"
                    style={{ border: 'none', flex: 1, padding: '0.75rem', outline: 'none' }}
                  />
                </div>
              </div>

              {user?.role && ['franchise', 'franchise_tags'].includes(user.role) && (
                <>
                  <div className="form-group">
                    <label htmlFor="business_name">Business Name</label>
                    <input
                      type="text"
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="Restaurant/Agency Name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="logo_url">Logo URL</label>
                    <input
                      type="url"
                      id="logo_url"
                      name="logo_url"
                      value={formData.logo_url}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Full Address *</label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      rows="3"
                      disabled={loading}
                      placeholder="Mandatory for franchises"
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                  style={{ flex: 1,color:'black' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default Dashboard


